import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { usePlayerStore } from '../../stores/usePlayerStore'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'

interface CopyrightRequest {
  id: string
  product_id: string
  brand_id: string
  status: 'pending' | 'approved' | 'rejected'
  request_message?: string
  admin_response?: string
  created_at: string
  reviewed_at?: string
  product?: {
    name: string
    category: string
  }
  brand?: {
    name: string
  }
}

export const CopyrightManagement: React.FC = () => {
  const { t } = useTranslation()
  const { player } = usePlayerStore()
  const [requests, setRequests] = useState<CopyrightRequest[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newRequest, setNewRequest] = useState({
    product_id: '',
    brand_id: '',
    request_message: ''
  })

  // Mock data
  const mockProducts = [
    { id: 'prod_1', name: 'نان بربری', category: 'food' },
    { id: 'prod_2', name: 'شیر تازه', category: 'food' },
    { id: 'prod_3', name: 'تخم مرغ محلی', category: 'food' }
  ]

  const mockBrands = [
    { id: 'brand_1', name: 'برند طلایی' },
    { id: 'brand_2', name: 'برند نقره‌ای' }
  ]

  useEffect(() => {
    if (player?.id) {
      loadRequests()
    }
  }, [player?.id])

  const loadRequests = async () => {
    setIsLoading(true)
    try {
      const response = await fetch(`https://business-tycoon-worker.smnshzh.workers.dev/api/copyright/requests?player_id=${player?.id}`)
      const data = await response.json()
      
      if (data.success) {
        setRequests(data.data || [])
      } else {
        console.error('Failed to load requests:', data.error)
        setRequests([])
      }
    } catch (error) {
      console.error('Error loading requests:', error)
      setRequests([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateRequest = async () => {
    if (!newRequest.request_message.trim()) {
      alert(t('copyright.fillRequiredFields'))
      return
    }

    setIsLoading(true)
    try {
      const response = await fetch('https://business-tycoon-worker.smnshzh.workers.dev/api/copyright/requests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          player_id: player?.id,
          product_id: newRequest.product_id || null,
          brand_id: newRequest.brand_id || null,
          request_message: newRequest.request_message
        }),
      })

      const data = await response.json()
      
      if (data.success) {
        setRequests([data.data, ...requests])
        setShowCreateForm(false)
        setNewRequest({ product_id: '', brand_id: '', request_message: '' })
        alert(t('copyright.requestSubmitted'))
      } else {
        alert(t('copyright.requestError') + ': ' + data.error)
      }
    } catch (error) {
      console.error('Error creating request:', error)
      alert(t('copyright.requestError'))
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100'
      case 'rejected': return 'text-red-600 bg-red-100'
      default: return 'text-yellow-600 bg-yellow-100'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'approved': return t('copyright.approved')
      case 'rejected': return t('copyright.rejected')
      default: return t('copyright.pending')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card title={`🛡️ ${t('copyright.title')}`} subtitle={t('copyright.subtitle')}>
        <div className="flex justify-between items-center">
          <div>
            <p className="text-gray-600">
              {t('copyright.yourRequests')}: {requests.length} {t('copyright.requests')}
            </p>
            <p className="text-sm text-gray-500">
              {t('copyright.protectProducts')}
            </p>
          </div>
          <Button
            onClick={() => setShowCreateForm(true)}
            variant="primary"
          >
            {t('copyright.createRequest')}
          </Button>
        </div>
      </Card>

      {/* Create Request Form */}
      {showCreateForm && (
        <Card title={t('copyright.createRequest')} subtitle={t('copyright.createRequestSubtitle')}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                انتخاب محصول *
              </label>
              <select
                value={newRequest.product_id}
                onChange={(e) => setNewRequest({ ...newRequest, product_id: e.target.value })}
                className="input-field"
              >
                <option value="">انتخاب محصول</option>
                {mockProducts.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} ({product.category})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                انتخاب برند *
              </label>
              <select
                value={newRequest.brand_id}
                onChange={(e) => setNewRequest({ ...newRequest, brand_id: e.target.value })}
                className="input-field"
              >
                <option value="">انتخاب برند</option>
                {mockBrands.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                توضیحات درخواست
              </label>
              <textarea
                value={newRequest.request_message}
                onChange={(e) => setNewRequest({ ...newRequest, request_message: e.target.value })}
                className="input-field"
                rows={3}
                placeholder={t('copyright.requestMessagePlaceholder')}
                maxLength={500}
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleCreateRequest}
                disabled={!newRequest.product_id || !newRequest.brand_id}
                variant="primary"
              >
                ارسال درخواست
              </Button>
              <Button
                onClick={() => setShowCreateForm(false)}
                variant="secondary"
              >
                انصراف
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Requests List */}
      <Card title={t('copyright.yourRequests')} subtitle={t('copyright.yourRequestsSubtitle')}>
        {isLoading ? (
          <div className="flex justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : requests.length > 0 ? (
          <div className="space-y-4">
            {requests.map((request) => (
              <div
                key={request.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-800">
                      {request.product?.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      برند: {request.brand?.name}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request.status)}`}>
                    {getStatusLabel(request.status)}
                  </span>
                </div>

                {request.request_message && (
                  <p className="text-sm text-gray-700 mb-3 bg-gray-50 p-3 rounded">
                    {request.request_message}
                  </p>
                )}

                {request.admin_response && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">پاسخ ادمین:</p>
                    <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
                      {request.admin_response}
                    </p>
                  </div>
                )}

                <div className="flex justify-between items-center text-xs text-gray-500">
                  <span>تاریخ ارسال: {new Date(request.created_at).toLocaleDateString('fa-IR')}</span>
                  {request.reviewed_at && (
                    <span>تاریخ بررسی: {new Date(request.reviewed_at).toLocaleDateString('fa-IR')}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🛡️</span>
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              {t('copyright.noRequests')}
            </h3>
            <p className="text-gray-500 mb-4">
              {t('copyright.noRequestsMessage')}
            </p>
            <Button
              onClick={() => setShowCreateForm(true)}
              variant="primary"
            >
              {t('copyright.createFirstRequest')}
            </Button>
          </div>
        )}
      </Card>

      {/* Copyright Benefits */}
      <Card title={t('copyright.benefits')} subtitle={t('copyright.benefitsSubtitle')}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start p-4 bg-green-50 rounded-lg">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center ml-3">
              <span className="text-green-600">🔒</span>
            </div>
            <div>
              <h4 className="font-medium text-green-800">{t('copyright.legalProtection')}</h4>
              <p className="text-sm text-green-700">
                {t('copyright.legalProtectionDescription')}
              </p>
            </div>
          </div>

          <div className="flex items-start p-4 bg-blue-50 rounded-lg">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center ml-3">
              <span className="text-blue-600">💰</span>
            </div>
            <div>
              <h4 className="font-medium text-blue-800">{t('copyright.additionalRevenue')}</h4>
              <p className="text-sm text-blue-700">
                {t('copyright.additionalRevenueDescription')}
              </p>
            </div>
          </div>

          <div className="flex items-start p-4 bg-purple-50 rounded-lg">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center ml-3">
              <span className="text-purple-600">⭐</span>
            </div>
            <div>
              <h4 className="font-medium text-purple-800">{t('copyright.brandCredibility')}</h4>
              <p className="text-sm text-purple-700">
                {t('copyright.brandCredibilityDescription')}
              </p>
            </div>
          </div>

          <div className="flex items-start p-4 bg-orange-50 rounded-lg">
            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center ml-3">
              <span className="text-orange-600">⚖️</span>
            </div>
            <div>
              <h4 className="font-medium text-orange-800">{t('copyright.legalRight')}</h4>
              <p className="text-sm text-orange-700">
                {t('copyright.legalRightDescription')}
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}
